import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { CA_COUNTIES as COUNTIES_REF } from '@lds-efsp/shared-contracts';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import PricingEstimate from '../components/PricingEstimate';
import PartyFields, { type PartyData } from '../components/PartyFields';
import { type AddressData } from '../components/AddressFields';
import FormInput, { FormSelect, FormCheckbox } from '../../setup/components/FormInput';

type Step = 'filing-info' | 'case-info' | 'serve' | 'documents' | 'review';

const STEPS: { id: Step; label: string }[] = [
  { id: 'filing-info', label: 'Filing Info' },
  { id: 'documents', label: 'Documents' },
  { id: 'case-info', label: 'Case Info' },
  { id: 'serve', label: 'Serve After Filing' },
  { id: 'review', label: 'Review & Submit' },
];

const countyOptions = [
  { value: '', label: 'Select county...' },
  ...COUNTIES_REF.map(c => ({ value: c.name, label: c.name })),
];

interface CourtFilingFormProps {
  brand: BrandConfig;
}

const CourtFilingForm: React.FC<CourtFilingFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('filing-info');
  const {
    caseNumber,
    caseTitle,
    priority,
    typeData,
    documents,
    updateCommonFields,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as {
    filingState?: string;
    filingCounty?: string;
    filingType?: string;
    caseCategory?: string;
    isFederalOrFamily?: boolean;
    isNonEFile?: boolean;
    eServeCaseParticipants?: boolean;
    serveAfterFiling?: boolean;
    serveData?: {
      parties?: PartyData[];
      serveAddresses?: (AddressData & { serviceLevel: string; addressType: string })[];
    };
  };

  // Skip serve step if not serving after filing
  const visibleSteps = STEPS.filter(s => s.id !== 'serve' || data.serveAfterFiling);
  const visibleIndex = visibleSteps.findIndex(s => s.id === currentStep);

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        {/* Step navigation */}
        <div className="flex border-b mb-6" style={{ borderColor: brand.colors.border }}>
          {visibleSteps.map((step, i) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(step.id)}
              className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: currentStep === step.id ? brand.colors.primary : 'transparent',
                color: currentStep === step.id ? brand.colors.link : brand.colors.textLight,
              }}
            >
              {i + 1}. {step.label}
            </button>
          ))}
        </div>

        {currentStep === 'filing-info' && (
          <div className="space-y-6">
            <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Filing Information</h3>
              <FormInput brand={brand} label="State" name="filingState" value={data.filingState ?? 'CA'} onChange={(e) => updateTypeData({ filingState: e.target.value } as Partial<typeof typeData>)} required />
              <FormSelect brand={brand} label="County" name="filingCounty" value={data.filingCounty ?? ''} onChange={(e) => updateTypeData({ filingCounty: e.target.value } as Partial<typeof typeData>)} options={countyOptions} required />
              <FormSelect brand={brand} label="Filing Type" name="filingType" value={data.filingType ?? ''} onChange={(e) => updateTypeData({ filingType: e.target.value } as Partial<typeof typeData>)} options={[
                { value: '', label: 'Select filing type...' },
                { value: 'case-initiation', label: 'Case Initiation' },
                { value: 'subsequent-filing', label: 'Subsequent Filing' },
              ]} required />
              <FormSelect brand={brand} label="Case Category" name="caseCategory" value={data.caseCategory ?? ''} onChange={(e) => updateTypeData({ caseCategory: e.target.value } as Partial<typeof typeData>)} options={[
                { value: '', label: 'Select category...' },
                { value: 'family', label: 'Family' },
                { value: 'non-family', label: 'Non-Family' },
              ]} />
              <FormCheckbox brand={brand} label="Federal or Family court" name="isFederalOrFamily" checked={data.isFederalOrFamily ?? false} onChange={(e) => updateTypeData({ isFederalOrFamily: e.target.checked } as Partial<typeof typeData>)} />
              <FormCheckbox brand={brand} label="Non-eFiling" name="isNonEFile" checked={data.isNonEFile ?? false} onChange={(e) => updateTypeData({ isNonEFile: e.target.checked } as Partial<typeof typeData>)} />
              <FormCheckbox brand={brand} label="eServe case participants" name="eServeCaseParticipants" checked={data.eServeCaseParticipants ?? false} onChange={(e) => updateTypeData({ eServeCaseParticipants: e.target.checked } as Partial<typeof typeData>)} />
              <FormCheckbox brand={brand} label="Serve after filing" name="serveAfterFiling" checked={data.serveAfterFiling ?? false} onChange={(e) => updateTypeData({ serveAfterFiling: e.target.checked } as Partial<typeof typeData>)} />
            </div>
            <PricingEstimate brand={brand} county={data.filingCounty ?? ''} priority={priority} />
          </div>
        )}

        {currentStep === 'case-info' && (
          <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Case Information</h3>
            <FormInput brand={brand} label="Case Number" name="caseNumber" value={caseNumber} onChange={(e) => updateCommonFields({ caseNumber: e.target.value })} required />
            <FormInput brand={brand} label="Case Title" name="caseTitle" value={caseTitle} onChange={(e) => updateCommonFields({ caseTitle: e.target.value })} required />
          </div>
        )}

        {currentStep === 'serve' && data.serveAfterFiling && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold" style={{ color: brand.colors.text }}>Serve After Filing</h3>
            {(data.serveData?.parties ?? [{ name: '', capacity: 'individual' }]).map((party, i) => (
              <PartyFields
                key={`party-${party.name}-${party.capacity}`}
                brand={brand}
                index={i}
                value={party}
                onChange={(updated) => {
                  const current = data.serveData?.parties ?? [{ name: '', capacity: 'individual' }];
                  const newParties = [...current];
                  newParties[i] = updated;
                  updateTypeData({ serveData: { ...data.serveData, parties: newParties } } as Partial<typeof typeData>);
                }}
                onRemove={() => {
                  const current = data.serveData?.parties ?? [];
                  updateTypeData({ serveData: { ...data.serveData, parties: current.filter((_, idx) => idx !== i) } } as Partial<typeof typeData>);
                }}
                showRemove={(data.serveData?.parties ?? []).length > 1}
              />
            ))}
            <button
              onClick={() => {
                const current = data.serveData?.parties ?? [];
                updateTypeData({ serveData: { ...data.serveData, parties: [...current, { name: '', capacity: 'individual' }] } } as Partial<typeof typeData>);
              }}
              className="text-sm font-medium"
              style={{ color: brand.colors.link }}
            >
              + Add Party
            </button>
          </div>
        )}

        {currentStep === 'documents' && (
          <DocumentUploader brand={brand} documents={documents} onUpload={uploadDocument} onRemove={removeDocument} />
        )}

        {currentStep === 'review' && (
          <OrderSummaryPanel brand={brand} onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch { /* Error displayed in panel */ }
          }} />
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center pt-4 mt-6 border-t" style={{ borderColor: brand.colors.border }}>
          <button
            onClick={() => { if (visibleIndex > 0) setCurrentStep(visibleSteps[visibleIndex - 1].id); }}
            disabled={visibleIndex === 0}
            className="px-6 py-2 rounded-lg text-sm font-medium border"
            style={{ borderColor: brand.colors.border, opacity: visibleIndex === 0 ? 0.5 : 1 }}
          >
            Previous
          </button>
          {visibleIndex < visibleSteps.length - 1 && (
            <button
              onClick={() => setCurrentStep(visibleSteps[visibleIndex + 1].id)}
              className="px-6 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Next
            </button>
          )}
        </div>
      </div>

      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel brand={brand} onSubmit={async () => {
          try {
            await submitOrder();
            navigate('/orders');
          } catch { /* Error displayed in panel */ }
        }} />
      </div>
    </div>
  );
};

export default CourtFilingForm;
